import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';

// Comprehensive list of major cities and countries
const locations = [
  { city: "New York", country: "USA", code: "NYC" },
  { city: "London", country: "UK", code: "LON" },
  { city: "Paris", country: "France", code: "PAR" },
  { city: "Tokyo", country: "Japan", code: "TYO" },
  { city: "Dubai", country: "UAE", code: "DXB" },
  { city: "Singapore", country: "Singapore", code: "SIN" },
  { city: "Sydney", country: "Australia", code: "SYD" },
  { city: "Toronto", country: "Canada", code: "YYZ" },
  { city: "Berlin", country: "Germany", code: "BER" },
  { city: "Mumbai", country: "India", code: "BOM" },
  { city: "Hong Kong", country: "China", code: "HKG" },
  { city: "Bangkok", country: "Thailand", code: "BKK" },
  { city: "Istanbul", country: "Turkey", code: "IST" },
  { city: "Seoul", country: "South Korea", code: "ICN" },
  { city: "Amsterdam", country: "Netherlands", code: "AMS" },
  { city: "Barcelona", country: "Spain", code: "BCN" },
  { city: "Rome", country: "Italy", code: "ROM" },
  { city: "Cairo", country: "Egypt", code: "CAI" },
  { city: "Moscow", country: "Russia", code: "MOW" },
  { city: "Vienna", country: "Austria", code: "VIE" },
  // Add more cities as needed
];

function TripForm({ onSubmit, disabled }) {
  const [formData, setFormData] = useState({
    travelers: 1,
    flyingFrom: null,
    flyingTo: null,
    fromDate: '',
    toDate: '',
    budget: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState({ from: '', to: '' });

  // Function to get location string representation
  const getLocationString = (location) => {
    return location ? `${location.city}, ${location.country}` : '';
  };

  // Fuzzy search function
  const filterLocations = (inputValue) => {
    const searchTerm = inputValue.toLowerCase();
    return locations.filter(
      location =>
        location.city.toLowerCase().includes(searchTerm) ||
        location.country.toLowerCase().includes(searchTerm) ||
        location.code.toLowerCase().includes(searchTerm)
    );
  };

  const getCurrentLocation = async () => {
    setLoading(true);
    setError('');
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      
      const { latitude, longitude } = position.coords;
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      const data = await response.json();
      
      // Find the closest matching city in our locations list
      const closestCity = locations.find(
        loc => loc.city.toLowerCase().includes(data.city.toLowerCase()) ||
              loc.country.toLowerCase().includes(data.countryName.toLowerCase())
      ) || { city: data.city, country: data.countryName };

      setFormData(prev => ({
        ...prev,
        flyingFrom: closestCity
      }));
      setInputValue(prev => ({
        ...prev,
        from: getLocationString(closestCity)
      }));
    } catch (err) {
      setError('Unable to get current location. Please enter manually.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTravelersChange = (increment) => {
    setFormData(prev => ({
      ...prev,
      travelers: Math.max(1, prev.travelers + increment)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      flyingFrom: getLocationString(formData.flyingFrom),
      flyingTo: getLocationString(formData.flyingTo)
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h5" gutterBottom>
          Plan Your Trip
        </Typography>

        {/* Number of travelers */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Number of travelers</Typography>
          <IconButton 
            onClick={() => handleTravelersChange(-1)} 
            disabled={formData.travelers <= 1 || disabled}
          >
            <RemoveIcon />
          </IconButton>
          <Typography>{formData.travelers}</Typography>
          <IconButton 
            onClick={() => handleTravelersChange(1)}
            disabled={disabled}
          >
            <AddIcon />
          </IconButton>
        </Box>

        {/* Flying from with Autocomplete */}
        <Box sx={{ position: 'relative' }}>
          <Autocomplete
            value={formData.flyingFrom}
            onChange={(event, newValue) => {
              setFormData(prev => ({ ...prev, flyingFrom: newValue }));
            }}
            inputValue={inputValue.from}
            onInputChange={(event, newInputValue) => {
              setInputValue(prev => ({ ...prev, from: newInputValue }));
            }}
            options={filterLocations(inputValue.from)}
            getOptionLabel={getLocationString}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Flying from"
                required
                disabled={disabled || loading}
                error={!!error}
                helperText={error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <Button
                        onClick={getCurrentLocation}
                        disabled={disabled || loading}
                        sx={{ ml: 1 }}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Current Location'}
                      </Button>
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Flying to with Autocomplete */}
        <Autocomplete
          value={formData.flyingTo}
          onChange={(event, newValue) => {
            setFormData(prev => ({ ...prev, flyingTo: newValue }));
          }}
          inputValue={inputValue.to}
          onInputChange={(event, newInputValue) => {
            setInputValue(prev => ({ ...prev, to: newInputValue }));
          }}
          options={filterLocations(inputValue.to)}
          getOptionLabel={getLocationString}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Flying to"
              required
              disabled={disabled}
            />
          )}
        />

        {/* Dates */}
        <TextField
          fullWidth
          label="From Date"
          name="fromDate"
          type="date"
          value={formData.fromDate}
          onChange={handleChange}
          required
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          label="To Date"
          name="toDate"
          type="date"
          value={formData.toDate}
          onChange={handleChange}
          required
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />

        {/* Budget */}
        <TextField
          fullWidth
          label="Budget"
          name="budget"
          type="number"
          value={formData.budget}
          onChange={handleChange}
          required
          disabled={disabled}
          InputProps={{
            startAdornment: '$',
          }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={disabled}
          sx={{ mt: 2 }}
        >
          Plan my Trip!
        </Button>
      </Stack>
    </Box>
  );
}

export default TripForm;
